// Building and sending sealed messages (M28, D59, D61). Text, attachments,
// edits and forwards all go through here when this device has its key.
import {
  api,
  ApiError,
  type UploadedAttachment,
  type UploadOptions,
} from '../api';
import { gatheringContentSchema } from '@twinden/shared';
import { t } from '../../i18n';
import { sealedPreviewFor } from '../previewImage';
import { encryptFile, type FileSecret } from './crypto';
import {
  forgetScope,
  isE2EEReady,
  nextRevision,
  seal,
  sendSealed,
  type Envelope,
} from './session';
import type { QueuedRequest } from '../../store/chat';

export { isE2EEReady };

const LINK = /https?:\/\/[^\s<>"']+/i;

/**
 * The sender's device fetches a link's preview (the server doesn't keep it)
 * and puts it inside the envelope, so nobody else's device has to ask (D61);
 * its picture goes in as a small sealed copy, never as the site's address
 * (D318).
 */
async function previewFor(text: string): Promise<unknown | undefined> {
  const url = LINK.exec(text)?.[0]?.replace(/[).,;:!?]+$/, '');
  if (!url) return undefined;
  return (await sealedPreviewFor(url)) ?? undefined;
}

export interface Mentions {
  mentions?: string[];
  mentionsEveryone?: boolean;
}

export async function sendSealedText(
  input: {
    conversationId: string;
    clientId: string;
    text: string;
    replyToId?: string;
  } & Mentions
) {
  const linkPreview = await previewFor(input.text);
  const envelope: Envelope = {
    v: 1,
    text: input.text,
    ...(linkPreview ? { meta: { linkPreview } } : {}),
  };
  return sendSealed(
    input.conversationId,
    input.clientId,
    envelope,
    { type: 'text' },
    (sealed) =>
      api.sendMessage({
        conversationId: input.conversationId,
        clientId: input.clientId,
        replyToId: input.replyToId,
        content: sealed.content,
        keyEpoch: sealed.keyEpoch,
        encryption: sealed.encryption,
        mentions: input.mentions,
        mentionsEveryone: input.mentionsEveryone,
      })
  );
}

async function textEnvelope(text: string): Promise<Envelope> {
  const linkPreview = await previewFor(text);
  return { v: 1, text, ...(linkPreview ? { meta: { linkPreview } } : {}) };
}

/**
 * The outbox's request for a new text message (M29, D68): sealed here when
 * this device has its key, with cached key information when offline (D67).
 */
export async function textRequest(
  input: {
    conversationId: string;
    clientId: string;
    text: string;
    replyToId?: string;
  } & Mentions
): Promise<QueuedRequest> {
  const base = {
    conversationId: input.conversationId,
    clientId: input.clientId,
    replyToId: input.replyToId,
  };
  if (!isE2EEReady()) {
    return {
      method: 'POST',
      path: '/messages',
      body: { ...base, content: input.text },
    };
  }
  const sealed = await seal(
    input.conversationId,
    input.clientId,
    await textEnvelope(input.text),
    { type: 'text' }
  );
  return {
    method: 'POST',
    path: '/messages',
    body: {
      ...base,
      content: sealed.content,
      keyEpoch: sealed.keyEpoch,
      encryption: sealed.encryption,
      mentions: input.mentions,
      mentionsEveryone: input.mentionsEveryone,
    },
  };
}

/** What a poll's sealed content holds (D220). */
export function pollEnvelope(question: string, options: string[]): Envelope {
  return { v: 1, poll: { question, options } };
}

/**
 * The outbox's request for a new poll (M60, D220). Polls are always sealed:
 * without this device's key it waits (and is tried again) rather than
 * going in plaintext.
 */
export async function pollRequest(input: {
  conversationId: string;
  clientId: string;
  question: string;
  options: string[];
  multiple: boolean;
  closesInSeconds: number | null;
  replyToId?: string;
}): Promise<QueuedRequest> {
  if (!isE2EEReady()) {
    throw new ApiError(t('chat.poll.needsKeys'), 'KEYS_NOT_READY', 0);
  }
  const sealed = await seal(
    input.conversationId,
    input.clientId,
    pollEnvelope(input.question, input.options),
    { type: 'poll' }
  );
  return {
    method: 'POST',
    path: '/messages',
    body: {
      conversationId: input.conversationId,
      clientId: input.clientId,
      replyToId: input.replyToId,
      type: 'poll',
      content: sealed.content,
      keyEpoch: sealed.keyEpoch,
      encryption: sealed.encryption,
      metadata: {
        poll: {
          options: input.options.length,
          multiple: input.multiple,
          closesInSeconds: input.closesInSeconds,
        },
      },
    },
  };
}

/**
 * What a gathering's sealed content holds (D282): its title, and its place
 * and note when they say something.
 */
export function gatheringEnvelope(content: {
  title: string;
  place?: string;
  note?: string;
}): Envelope {
  const place = content.place?.trim();
  const note = content.note?.trim();
  return {
    v: 1,
    gathering: {
      title: content.title.trim(),
      ...(place ? { place } : {}),
      ...(note ? { note } : {}),
    },
  };
}

/**
 * The outbox's request for a new gathering (M71, D282). Like a poll, it's
 * always sealed: without this device's key it waits (and is tried again)
 * rather than going in plaintext. Only when it starts and whether it may
 * ring go in plain sight; the server needs them for reminders and rings.
 */
export async function gatheringRequest(input: {
  conversationId: string;
  clientId: string;
  title: string;
  place?: string;
  note?: string;
  startsAt: string;
  rings: boolean;
  replyToId?: string;
}): Promise<QueuedRequest> {
  if (!isE2EEReady()) {
    throw new ApiError(t('chat.gathering.needsKeys'), 'KEYS_NOT_READY', 0);
  }
  // Checked before sealing: words that break the limits would seal into
  // something nobody can open (the opener drops them, D282). A 400 isn't
  // retried, so the entry is rejected and the person sees why.
  if (
    !gatheringContentSchema.safeParse(gatheringEnvelope(input).gathering)
      .success
  ) {
    throw new ApiError(t('chat.gathering.invalid'), 'GATHERING_INVALID', 400);
  }
  const sealed = await seal(
    input.conversationId,
    input.clientId,
    gatheringEnvelope(input),
    { type: 'gathering' }
  );
  return {
    method: 'POST',
    path: '/messages',
    body: {
      conversationId: input.conversationId,
      clientId: input.clientId,
      replyToId: input.replyToId,
      type: 'gathering',
      content: sealed.content,
      keyEpoch: sealed.keyEpoch,
      encryption: sealed.encryption,
      metadata: {
        gathering: { startsAt: input.startsAt, rings: input.rings },
      },
    },
  };
}

/**
 * The outbox's request for an edit, sealed under the message's clientId
 * for the next revision (D326).
 */
export async function editRequest(
  input: {
    messageId: string;
    conversationId: string;
    messageClientId?: string;
    /** The server's revision after STALE_REVISION (D326). */
    atLeastRevision?: number;
    text: string;
  } & Mentions
): Promise<QueuedRequest> {
  const path = `/messages/${input.messageId}`;
  if (!isE2EEReady() || !input.messageClientId) {
    return { method: 'PATCH', path, body: { content: input.text } };
  }
  const revision = nextRevision(
    input.conversationId,
    input.messageClientId,
    input.atLeastRevision
  );
  const sealed = await seal(
    input.conversationId,
    input.messageClientId,
    await textEnvelope(input.text),
    { type: 'text', revision }
  );
  return {
    method: 'PATCH',
    path,
    body: {
      content: sealed.content,
      encryption: sealed.encryption,
      keyEpoch: sealed.keyEpoch,
      revision,
      mentions: input.mentions,
      mentionsEveryone: input.mentionsEveryone,
    },
  };
}

/**
 * Edits are sealed again under the message's own clientId, for the next
 * revision (D326); once more for the one after the server's if another
 * edit got there first (STALE_REVISION).
 */
export async function editSealed(
  input: {
    messageId: string;
    conversationId: string;
    clientId: string;
    text: string;
  } & Mentions
) {
  const linkPreview = await previewFor(input.text);
  const envelope: Envelope = {
    v: 1,
    text: input.text,
    ...(linkPreview ? { meta: { linkPreview } } : {}),
  };
  const attempt = (revision: number) =>
    sendSealed(
      input.conversationId,
      input.clientId,
      envelope,
      { type: 'text', revision },
      (sealed) =>
        api.editMessage(input.messageId, sealed.content, {
          keyEpoch: sealed.keyEpoch,
          encryption: sealed.encryption,
          revision,
          mentions: input.mentions,
          mentionsEveryone: input.mentionsEveryone,
        })
    );
  try {
    return await attempt(nextRevision(input.conversationId, input.clientId));
  } catch (err) {
    const stored = (err as ApiError & { details?: { revision?: unknown } })
      .details?.revision;
    if (
      err instanceof ApiError &&
      err.code === 'STALE_REVISION' &&
      typeof stored === 'number'
    ) {
      return attempt(
        nextRevision(input.conversationId, input.clientId, stored)
      );
    }
    throw err;
  }
}

/**
 * Encrypts a file (and its preview) on this device and uploads only the
 * ciphertext; the keys go into the envelope (D59).
 */
export async function uploadSealed(
  file: Blob,
  thumbnail: Blob | undefined,
  options: UploadOptions
): Promise<{
  upload: UploadedAttachment;
  file: FileSecret;
  thumb?: FileSecret;
}> {
  const main = await encryptFile(file);
  const preview = thumbnail ? await encryptFile(thumbnail) : undefined;
  // A neutral name: the real one travels in the envelope.
  const upload = await api.uploadAttachment(
    main.blob,
    'encrypted.bin',
    {},
    preview?.blob,
    options
  );
  return { upload, file: main.secret, thumb: preview?.secret };
}

/**
 * A single attachment's sealed envelope and its plain file list (D59): the
 * file's keys and everything about it, a voice note's peaks included
 * (D221), are inside; only the upload paths are in plain sight.
 */
export function attachmentEnvelope(input: {
  upload: UploadedAttachment;
  file: FileSecret;
  thumb?: FileSecret;
  meta: NonNullable<Envelope['meta']>;
}): { envelope: Envelope; files: string[] } {
  return {
    envelope: {
      v: 1,
      meta: {
        ...input.meta,
        file: input.file,
        ...(input.thumb ? { thumb: input.thumb } : {}),
      },
    },
    files: [
      input.upload.url,
      ...(input.upload.thumbnailUrl ? [input.upload.thumbnailUrl] : []),
    ],
  };
}

export async function sendSealedAttachment(input: {
  conversationId: string;
  clientId: string;
  type: string;
  replyToId?: string;
  upload: UploadedAttachment;
  file: FileSecret;
  thumb?: FileSecret;
  meta: NonNullable<Envelope['meta']>;
}) {
  const { envelope, files } = attachmentEnvelope(input);
  return sendSealed(
    input.conversationId,
    input.clientId,
    envelope,
    { type: input.type },
    (sealed) =>
      api.sendMessage({
        conversationId: input.conversationId,
        clientId: input.clientId,
        replyToId: input.replyToId,
        type: input.type,
        content: sealed.content,
        keyEpoch: sealed.keyEpoch,
        encryption: sealed.encryption,
        metadata: { files },
      })
  );
}

/**
 * Forwards a message to several chats, each copy sealed here for its chat,
 * bound to me, a fresh clientId and the original's type (D61, D325). The
 * server checks every target before answering; the chats it names as
 * needing a new or newer key are fetched again (rotating where asked, D58)
 * and every copy sealed again, once — the `sendSealed` retry, for many.
 */
export async function forwardSealed(
  messageId: string,
  conversationIds: string[],
  envelope: Envelope,
  type: string
) {
  const sealAll = () =>
    Promise.all(
      conversationIds.map(async (conversationId) => {
        const clientId = crypto.randomUUID();
        const sealed = await seal(conversationId, clientId, envelope, {
          type,
        });
        return { conversationId, clientId, ...sealed };
      })
    );
  try {
    return await api.forwardMessage(
      messageId,
      conversationIds,
      await sealAll()
    );
  } catch (err) {
    if (
      !(err instanceof ApiError) ||
      (err.code !== 'STALE_KEY' && err.code !== 'ROTATION_REQUIRED')
    ) {
      throw err;
    }
    const targets = (err.details?.targets ?? []) as Array<{
      conversationId?: unknown;
    }>;
    const named = targets
      .map((target) => target.conversationId)
      .filter((id): id is string => typeof id === 'string');
    for (const conversationId of named.length > 0 ? named : conversationIds) {
      forgetScope('chat', conversationId);
    }
    return api.forwardMessage(messageId, conversationIds, await sealAll());
  }
}
